import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api } from "../../api";
import { GUIDELINES_ENABLED } from "../../config";
import { Icon, Empty, Spinner } from "../../components/ui";
import { uiSwitch } from "../../helpers";
import { derive, parseDate, applyRefData } from "./recordDerive";
import { RecordModal } from "./Inventory";
import { IssueModal } from "../../components/IssueModal";

const STAGE_LABEL = { SUBMISSION: "Submission", END_PROCESS: "End Process", MODIFICATION: "Modification", MORE_INFO: "More Info Needed" };
const PA_STATUSES = ["PA Not Required", "Approved", "Canceled", "Denied", "Reviewed", "Pending", "Withdraw", "Faxed", "Submitted via call"];

function shortUid(uid) { return !uid ? "—" : uid.length > 16 ? "…" + uid.slice(-12) : uid; }
const ROW_CAP = 500;   // render at most this many rows (keeps the table snappy); the count shows the full total

export function Historical() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [fGroup, setFGroup] = useState("");
  const [fStage, setFStage] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fUser, setFUser] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  // Historical filtering is client-side (instant) — flash the one app-level loader briefly on each filter
  // change for feedback (uiSwitch, same overlay App renders; no second spinner of its own).
  const firstFilter = useRef(true);
  useEffect(() => {
    if (firstFilter.current) { firstFilter.current = false; return; }
    uiSwitch.trigger(350);
  }, [q, fGroup, fStage, fStatus, fUser, fFrom, fTo]);
  const [viewing, setViewing] = useState(null);
  const [glCodes, setGlCodes] = useState({});   // recordId -> guideline id used (any user)
  const [menu, setMenu] = useState(null);
  const [sendReq, setSendReq] = useState(null);
  const [sendBusy, setSendBusy] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [recs, ref] = await Promise.all([api.historicalRecords(), api.getRefData().catch(() => null)]);
      applyRefData(ref); setRows(recs);
      if (GUIDELINES_ENABLED) { try { const codes = await api.guidelineCodesForRecords((recs || []).map((r) => r.id)); setGlCodes(codes || {}); } catch { /* ignore */ } }
    } catch (e) { setErr(e.message); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const derived = useMemo(() => { const m = new Map(); (rows || []).forEach((r) => m.set(r.id, derive(r))); return m; }, [rows]);
  const groups = useMemo(() => Array.from(new Set((rows || []).map((r) => (derived.get(r.id) || {}).location).filter(Boolean))), [rows, derived]);
  const users = useMemo(() => Array.from(new Set((rows || []).map((r) => r.workedByName || r.assignedUserName).filter(Boolean))).sort(), [rows]);

  const onOrAfter = (value, from) => { if (!from) return true; const d = parseDate(value), f = parseDate(from); return d && f ? d >= f : true; };

  const filtered = useMemo(() => (rows || []).filter((r) => {
    const d = derived.get(r.id) || {};
    // only records that a user has actually worked (in a working stage or completed) — no unassigned/untouched
    const worked = !!r.workedAt || r.status === "IN_PROGRESS" || r.status === "DONE" || !!r.paStatus;
    if (!worked) return false;
    if (fGroup && d.location !== fGroup) return false;
    if (fStage && (r.sheet || "SUBMISSION") !== fStage) return false;
    if (fStatus && (r.paStatus || "") !== fStatus) return false;
    if (fUser && (r.workedByName || r.assignedUserName) !== fUser) return false;
    // date range — filter on the date the record was worked (status date if set, else worked timestamp)
    if (fFrom || fTo) {
      let dv = null;
      if (r.statusDate) { const m = String(r.statusDate).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); if (m) dv = new Date(+m[3], +m[1] - 1, +m[2]); }
      if (!dv && r.workedAt) { const t = new Date(r.workedAt); if (!isNaN(t)) dv = new Date(t.getFullYear(), t.getMonth(), t.getDate()); }
      if (!dv) return false;
      if (fFrom) { const [y, mo, d2] = fFrom.split("-").map(Number); if (dv < new Date(y, mo - 1, d2)) return false; }
      if (fTo) { const [y, mo, d2] = fTo.split("-").map(Number); if (dv > new Date(y, mo - 1, d2)) return false; }
    }
    if (q) {
      const s = q.toLowerCase();
      if (![r.uniqueId, r.mrn, d.orders, d.location, r.paStatus, r.workedByName, r.assignedUserName].some((v) => (v || "").toString().toLowerCase().includes(s))) return false;
    }
    return true;
  }), [rows, derived, fGroup, fStage, fStatus, fUser, fFrom, fTo, q]);

  const shown = useMemo(() => filtered.slice(0, ROW_CAP), [filtered]);

  // Memoize the rendered rows so opening the record modal (or the context menu) doesn't re-render the whole table.
  const rowEls = useMemo(() => shown.map((r) => {
    const d = derived.get(r.id) || {};
    return (
      <tr key={r.id} className="row-click" onClick={() => setViewing(r)}
        onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, record: r }); }}>
        <td>{d.location || "—"}</td>
        <td className="cell-sub">{STAGE_LABEL[r.sheet] || "Submission"}</td>
        <td className="mono">{r.mrn}</td>
        <td className="wrap">{d.orders}</td>
        <td className="cell-sub">{d.orderDate || "—"}</td>
        <td>{d.urgentRoutine === "Urgent" ? <span style={{ color: "#EA580C", fontWeight: 700 }}>Urgent</span> : "Routine"}</td>
        <td>{r.paStatus || <span className="cell-sub">—</span>}</td>
        <td className="cell-sub wrap">{r.inboxValue || "—"}</td>
        <td className="cell-sub">{r.workedByName || r.assignedUserName || "—"}</td>
        <td className="cell-sub">{r.dateRequested || "—"}</td>
        <td className="mono uid-full" title={r.uniqueId}>{r.uniqueId || "—"}</td>
        {GUIDELINES_ENABLED && <td className="mono">{glCodes[r.id] === "NONE" ? <span className="cell-sub">No guideline</span> : (glCodes[r.id] || <span className="cell-sub">—</span>)}</td>}
      </tr>
    );
  }), [shown, derived, glCodes]);

  const clearFilters = () => { setFGroup(""); setFStage(""); setFStatus(""); setFUser(""); setFFrom(""); setFTo(""); setQ(""); };

  return (
    <div className="hist-full">
      <div className="filter-bar">
        <div className="search"><Icon.search /><input className="inp" placeholder="Search PID, order, status…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select className="inp" value={fGroup} onChange={(e) => setFGroup(e.target.value)}>
          <option value="">All groups</option>{groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className="inp" value={fStage} onChange={(e) => setFStage(e.target.value)}>
          <option value="">All processes</option>{Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="inp" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">All statuses</option>{PA_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="inp" value={fUser} onChange={(e) => setFUser(e.target.value)}>
          <option value="">All users</option>{users.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <label className="filter-date">From <input className="inp" type="date" value={fFrom} max={fTo || undefined} onChange={(e) => setFFrom(e.target.value)} /></label>
        <label className="filter-date">To <input className="inp" type="date" value={fTo} min={fFrom || undefined} onChange={(e) => setFTo(e.target.value)} /></label>
        <button className="link-btn" onClick={clearFilters}>Clear</button>
        {rows && <span className="hist-count">{filtered.length.toLocaleString()} record{filtered.length === 1 ? "" : "s"}</span>}
      </div>

      <div className="card"><div className="table-wrap grid-scroll">
        <table className="tbl hist-tbl">
          <thead><tr>
            <th>Group</th><th>Process</th><th>PID</th><th>Order</th><th>Order Date</th>
            <th>Urgent</th><th>Status</th><th>Inbox</th><th>Worked by</th><th>Submitted</th><th>UID</th>{GUIDELINES_ENABLED && <th>Guideline ID</th>}
          </tr></thead>
          <tbody>{rowEls}</tbody>
        </table>
        {rows === null && <div className="empty"><Spinner /></div>}
        {err && <div className="err-text" style={{ padding: 18 }}>{err}</div>}
        {rows && filtered.length === 0 && !err && <Empty icon={<Icon.history width="40" height="40" />} title="No records match" text="Adjust the filters above." />}
      </div>
      {rows && filtered.length > 0 && (
        <div className="rev-count">
          Showing <b>{shown.length.toLocaleString()}</b> of <b>{filtered.length.toLocaleString()}</b> record{filtered.length === 1 ? "" : "s"}
          {filtered.length > ROW_CAP ? <> · refine the filters to narrow the list</> : null}
        </div>
      )}</div>

      {menu && (
        <>
          <div className="ctx-backdrop" onClick={() => setMenu(null)} onContextMenu={(e) => { e.preventDefault(); setMenu(null); }} />
          <div className="ctx-menu" style={{ left: menu.x, top: menu.y }}>
            <button className="ctx-item" onClick={() => { setSendReq({ record: menu.record, stage: "MODIFICATION" }); setMenu(null); }}>Send to Modification</button>
            <button className="ctx-item" onClick={() => { setSendReq({ record: menu.record, stage: "MORE_INFO" }); setMenu(null); }}>Send to More Info</button>
          </div>
        </>
      )}
      {sendReq && (
        <IssueModal
          title={`Send to ${sendReq.stage === "MODIFICATION" ? "Modification" : "More Info"}`}
          sub="Pick whose issue caused this and why — it's stored so we can report client / patient / production issues."
          busy={sendBusy}
          onCancel={() => setSendReq(null)}
          onConfirm={async (type, reason) => {
            setSendBusy(true);
            try { await api.sendToStage([sendReq.record.id], sendReq.stage, type, reason); setSendReq(null); load(); }
            catch (e) { setErr(e.message); }
            finally { setSendBusy(false); }
          }} />
      )}

      {viewing && <RecordModal record={viewing} onClose={() => setViewing(null)} canEdit={true} />}
    </div>
  );
}